/**
 * Buyer: QA Quotations List – Quotation Review & Approval.
 * Fetches QA requests and quotations from backend; evaluate and approve final bids.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonService from '../../../services/common.service';
import { API_URLS } from '../../../constants/api-urls';
import '../../../assets/styles/buyer_qa_quotations_list.scss';

const FEE_PERCENT = 0.25;

/** Parse fee breakdown from additional_notes (first line with "Professional fee:" and "Subtotal:") */
function parseFeeBreakdown(additionalNotes) {
  if (!additionalNotes || typeof additionalNotes !== 'string') return null;
  const line = additionalNotes.split('\n')[0] || '';
  if (!line.includes('Professional fee:') || !line.includes('Subtotal:')) return null;
  const result = { baseInspectionFee: 0, travelLogistics: 0, regionalTaxes: 0, platformEscrowFee: 0, subtotal: 0, totalWithFee: 0 };
  const patterns = [
    { key: 'baseInspectionFee', regex: /Professional fee:\s*([\d.]+)/i },
    { key: 'travelLogistics', regex: /Travel\s*[&]\s*logistics:\s*([\d.]+)/i },
    { key: 'regionalTaxes', regex: /Taxes:\s*([\d.]+)/i },
    { key: 'subtotal', regex: /Subtotal:\s*([\d.]+)/i },
    { key: 'totalWithFee', regex: /Total\s*\(incl[^)]*\):\s*([\d.]+)/i },
  ];
  patterns.forEach(({ key, regex }) => {
    const m = line.match(regex);
    if (m) result[key] = parseFloat(m[1]) || 0;
  });
  result.platformEscrowFee = Math.round((result.subtotal * FEE_PERCENT) * 100) / 100;
  return result;
}

/** Contract manufacturing vs classic buyer–seller quotation QA (same approve/reject APIs). */
function isCmQaRequest(r) {
  if (!r) return false;
  if (r.context_source === 'contract_manufacturer') return true;
  const cmRfq = r.cm_rfq_id != null && Number(r.cm_rfq_id) > 0;
  const cmQ = r.cm_quotation_id != null && Number(r.cm_quotation_id) > 0;
  return cmRfq && cmQ;
}

function qaRequestSelectorLabel(r) {
  if (!r) return '';
  const num = r.qa_request_number || `QA-${r.qa_request_id}`;
  const status = r.status ? ` (${r.status})` : '';
  if (isCmQaRequest(r)) {
    const mfr = r.cm_manufacturer_name ? ` · ${r.cm_manufacturer_name}` : '';
    return `${num}${status} · Contract manufacturing${mfr}`;
  }
  return `${num}${status} · Supplier quotation`;
}

/** Format proposed period for timeline display */
function formatTimeline(proposedStart, proposedEnd) {
  if (!proposedStart && !proposedEnd) return '—';
  const start = proposedStart ? new Date(proposedStart).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—';
  const end = proposedEnd ? new Date(proposedEnd).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—';
  return `${start} – ${end}`;
}

export default function BuyerQAQuotationsList() {
  const navigate = useNavigate();
  const [qaRequests, setQaRequests] = useState([]);
  const [selectedQaRequestId, setSelectedQaRequestId] = useState(null);
  const [requestDetail, setRequestDetail] = useState(null);
  const [quotations, setQuotations] = useState([]);
  const [inspectorNames, setInspectorNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedQuotationId, setSelectedQuotationId] = useState(null);
  const [approveSubmitting, setApproveSubmitting] = useState(false);
  const [rejectSubmitting, setRejectSubmitting] = useState(false);

  const selectedQuotation = useMemo(
    () => quotations.find((q) => q.quotation_id === selectedQuotationId) || quotations[0],
    [quotations, selectedQuotationId]
  );

  const parsedBreakdown = useMemo(
    () => (selectedQuotation && parseFeeBreakdown(selectedQuotation.additional_notes)) || null,
    [selectedQuotation]
  );

  const currency = selectedQuotation?.currency || 'USD';
  const symbol = currency === 'USD' ? '$' : currency;
  const finalAmount = parsedBreakdown?.totalWithFee ?? selectedQuotation?.quote_amount ?? 0;
  const baseFee = parsedBreakdown?.baseInspectionFee ?? 0;
  const travelFee = parsedBreakdown?.travelLogistics ?? 0;
  const taxesFee = parsedBreakdown?.regionalTaxes ?? 0;
  const platformFee = parsedBreakdown?.platformEscrowFee ?? 0;

  useEffect(() => {
    let cancelled = false;
    async function fetchMyRequests() {
      try {
        const res = await CommonService.get(API_URLS.QA.BUYER_MY_REQUESTS);
        if (cancelled) return;
        const list = res?.data && Array.isArray(res.data) ? res.data : [];
        setQaRequests(list);
        if (list.length > 0 && !selectedQaRequestId) {
          setSelectedQaRequestId(list[0].qa_request_id);
        }
      } catch (e) {
        if (!cancelled) {
          toast.error('Failed to load QA requests');
          setQaRequests([]);
        }
      }
    }
    fetchMyRequests();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!selectedQaRequestId) {
      setRequestDetail(null);
      setQuotations([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [reqRes, quotRes] = await Promise.all([
          CommonService.get(API_URLS.QA.GET_BY_ID(selectedQaRequestId)),
          CommonService.get(API_URLS.QA.QUOTATIONS_BY_REQUEST(selectedQaRequestId)),
        ]);
        if (cancelled) return;
        setRequestDetail(reqRes?.data ?? null);
        const list = quotRes?.data && Array.isArray(quotRes.data) ? quotRes.data : [];
        setQuotations(list);
        setSelectedQuotationId(list[0]?.quotation_id ?? null);
        const inspectorIds = [...new Set(list.map((q) => q.inspector_user_id).filter(Boolean))];
        const names = {};
        await Promise.all(
          inspectorIds.map(async (uid) => {
            try {
              const profileRes = await CommonService.get(`/company/profile/${uid}`);
              if (cancelled) return;
              const profile = profileRes?.data ?? {};
              const name = profile.company_name || profile.name || `Inspector ${uid}`;
              names[uid] = name;
            } catch (_) {
              if (!cancelled) names[uid] = `Inspector ${uid}`;
            }
          })
        );
        if (!cancelled) setInspectorNames(names);
      } catch (e) {
        if (!cancelled) {
          toast.error('Failed to load quotations');
          setQuotations([]);
          setRequestDetail(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedQaRequestId]);

  const handleApproveAndPay = async () => {
    const quotationId = selectedQuotation?.quotation_id;
    if (!quotationId) {
      toast.error('No quotation selected.');
      return;
    }
    setApproveSubmitting(true);
    try {
      const res = await CommonService.post(
        API_URLS.QA.QUOTATION_ACCEPT(quotationId),
        {}
      );
      const inspectionId = res?.data?.inspectionId ?? res?.inspectionId;
      const paymentApproval = res?.data?.payment_approval ?? res?.payment_approval;
      if (paymentApproval?.status === 'pending') {
        toast.info(
          'Quotation approved. Payment is pending internal payment approval before you can complete payment.',
        );
      } else {
        toast.success('Quotation approved successfully.');
      }
      if (inspectionId) {
        navigate(`/qa/inspection/${inspectionId}/invoice`);
      } else {
        navigate('/buyer/qa-quotations-list');
      }
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Failed to approve quotation';
      toast.error(message);
    } finally {
      setApproveSubmitting(false);
    }
  };

  const handleRejectQuotation = async () => {
    const quotationId = selectedQuotation?.quotation_id;
    if (!quotationId) {
      toast.error('No quotation selected.');
      return;
    }
    setRejectSubmitting(true);
    try {
      await CommonService.post(API_URLS.QA.QUOTATION_REJECT(quotationId), {});
      toast.success('Quotation rejected.');
      setQuotations((prev) =>
        prev.map((q) => (q.quotation_id === quotationId ? { ...q, status: 'rejected' } : q))
      );
      const remaining = quotations.filter((q) => q.quotation_id !== quotationId);
      if (remaining.length > 0) setSelectedQuotationId(remaining[0].quotation_id);
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Failed to reject quotation';
      toast.error(message);
    } finally {
      setRejectSubmitting(false);
    }
  };

  const handleViewInvoice = () => {
    const inspectionId = selectedQuotation?.inspection_id;
    if (!inspectionId) {
      toast.error('Inspection not found for this quotation.');
      return;
    }
    navigate(`/qa/inspection/${inspectionId}/invoice`);
  };

  const handlePaymentNow = () => {
    const inspectionId = selectedQuotation?.inspection_id;
    if (!inspectionId) {
      toast.error('Inspection not found for this quotation.');
      return;
    }
    const inspectorName =
      inspectorNames[selectedQuotation?.inspector_user_id] ||
      `Inspector ${selectedQuotation?.inspector_user_id || ''}`;
    navigate(`/qa/inspection/${inspectionId}/payment`, {
      state: {
        inspectorName,
        inspectorRoleHub: 'Lead Inspector',
        totalQuote: finalAmount,
        currency,
      },
    });
  };

  const handleReviewReport = () => {
    const id = selectedQuotation?.inspection_id;
    if (!id) {
      toast.error('Inspection not found for this quotation.');
      return;
    }
    navigate(`/buyer/qa-inspection/${id}/review`);
  };

  const requestStatus = String(requestDetail?.status || '').trim();
  const inspectionCompleted =
    String(selectedQuotation?.inspection_status || '').trim().toLowerCase() === 'completed' ||
    requestStatus === 'Completed';
  const buyerDecision = selectedQuotation?.buyer_decision;
  const canReviewReport =
    Boolean(selectedQuotation?.inspection_id) &&
    inspectionCompleted &&
    !isCmQaRequest(requestDetail);
  const showReviewReportAction = canReviewReport && !buyerDecision;
  const showViewDecisionAction = canReviewReport && Boolean(buyerDecision);
  const selectedPaymentCtx = selectedQuotation?.buyer_payment_context;
  const selectedP2pPayment = selectedPaymentCtx?.p2p_payment;
  const isSelectedPaid =
    selectedP2pPayment?.buyer_payment_status === 'paid' ||
    String(selectedPaymentCtx?.buyer_payment_status || '').trim() === 'paid';
  const canShowPaymentNow =
    selectedP2pPayment?.is_p2p_linked === true
      ? selectedP2pPayment?.can_show_payment_now === true
      : Boolean(selectedQuotation?.inspection_id) && !isSelectedPaid;

  if (loading && qaRequests.length === 0) {
    return (
      <div className="buyer-qa-quotations-list">
        <div className="qa-quotations-container">
          <div className="qa-quotations-loading">
            <div className="qa-quotations-spinner" aria-hidden />
            <p>Loading your QA requests…</p>
          </div>
        </div>
      </div>
    );
  }

  if (qaRequests.length === 0) {
    return (
      <div className="buyer-qa-quotations-list">
        <div className="qa-quotations-container">
          <button type="button" className="qa-quotations-back" onClick={() => navigate('/dashboard/buyer')}>
            ← Back to Dashboard
          </button>
          <div className="qa-quotations-hero">
            <h1 className="qa-quotations-page-title">Quotation Review & Approval</h1>
            <p className="qa-quotations-page-subtitle">Evaluate and approve final bids from quality inspectors</p>
          </div>
          <div className="qa-quotations-empty">
            <div className="qa-quotations-empty-icon" aria-hidden>📋</div>
            <h3>No QA requests yet</h3>
            <p>
              Create a QA request from an accepted supplier quotation or from a contract-manufacturer RFQ to receive
              inspector bids, then review them here.
            </p>
            <button type="button" className="qa-quotations-btn qa-quotations-btn-approve" onClick={() => navigate('/dashboard/buyer')}>
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="buyer-qa-quotations-list">
      <div className="qa-quotations-container">
        <button type="button" className="qa-quotations-back" onClick={() => navigate('/dashboard/buyer')}>
          ← Back to Dashboard
        </button>
        <div className="qa-quotations-hero">
          <h1 className="qa-quotations-page-title">Quotation Review & Approval</h1>
          <p className="qa-quotations-page-subtitle">
            Evaluate and approve final bids from quality inspectors (supplier quotations and contract-manufacturing RFQs).
          </p>
        </div>

        {qaRequests.length > 1 && (
          <div className="qa-quotations-request-selector">
            <label htmlFor="qa-request-select">QA Request</label>
            <select
              id="qa-request-select"
              value={selectedQaRequestId || ''}
              onChange={(e) => setSelectedQaRequestId(Number(e.target.value) || null)}
            >
              {qaRequests.map((r) => (
                <option key={r.qa_request_id} value={r.qa_request_id}>
                  {qaRequestSelectorLabel(r)}
                </option>
              ))}
            </select>
          </div>
        )}

        {loading ? (
          <div className="qa-quotations-loading">
            <div className="qa-quotations-spinner" aria-hidden />
            <p>Loading quotations…</p>
          </div>
        ) : quotations.length === 0 ? (
          <div className="qa-quotations-empty qa-quotations-empty--inline">
            <div className="qa-quotations-empty-icon" aria-hidden>📩</div>
            <h3>No quotations yet</h3>
            <p>Inspector bids for this QA request will appear here once they submit quotes.</p>
          </div>
        ) : (
          <div className="qa-quotations-layout">
            <div className="qa-quotations-left">
              <h2 className="qa-quotations-section-heading">
                <span className="qa-quotations-section-heading-text">Received quotations</span>
                <span className="qa-quotations-section-heading-count">{quotations.length}</span>
              </h2>
              <div className="qa-quotation-cards">
                {quotations.map((q) => (
                  <div
                    key={q.quotation_id}
                    className={`qa-quotation-card ${selectedQuotationId === q.quotation_id ? 'qa-quotation-card--selected' : ''}`}
                    onClick={() => setSelectedQuotationId(q.quotation_id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && setSelectedQuotationId(q.quotation_id)}
                  >
                    <div className="qa-quotation-card-top">
                      <div className="qa-quotation-card-avatar">
                        <span className="qa-quotation-card-avatar-initial">
                          {(inspectorNames[q.inspector_user_id] || 'I').charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="qa-quotation-card-meta">
                        <div className="qa-quotation-card-name">{inspectorNames[q.inspector_user_id] || `Inspector ${q.inspector_user_id}`}</div>
                        <div className="qa-quotation-card-audits">Quote #{q.quotation_id}</div>
                        {q.status && (
                          <span className={`qa-quotation-card-status qa-quotation-card-status--${(q.status || '').toLowerCase().replace(/_/g, '-')}`}>
                            {q.status}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="qa-quotation-card-total">
                      {symbol}{Number(q.quote_amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </div>
                    <div className="qa-quotation-card-timeline-wrap">
                      <span className="qa-quotation-card-timeline">
                        {formatTimeline(q.proposed_start_date, q.proposed_end_date)}
                      </span>
                      <span className="qa-quotation-card-expand" aria-hidden>→</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="qa-quotations-right">
              <div className="qa-quotations-detail-header">
                <h2 className="qa-quotations-detail-title">Quotation detail</h2>
                <p className="qa-quotations-detail-subtitle">Cost breakdown and scope of work</p>
                {requestDetail && (
                  <div className="qa-quotations-detail-flow">
                    {isCmQaRequest(requestDetail) ? (
                      <span className="qa-quotations-flow-badge qa-quotations-flow-badge--cm">
                        Contract manufacturing
                        {requestDetail.cm_manufacturer_name ? ` · ${requestDetail.cm_manufacturer_name}` : ''}
                      </span>
                    ) : (
                      <span className="qa-quotations-flow-badge qa-quotations-flow-badge--classic">Supplier quotation</span>
                    )}
                  </div>
                )}
              </div>

              <section className="qa-quotations-scope">
                <h3 className="qa-quotations-scope-title">Proposed scope of work</h3>
                <p className="qa-quotations-scope-text">
                  {selectedQuotation?.inspection_approach || 'No scope description provided.'}
                </p>
              </section>

              <div className="qa-quotations-cost-breakdown">
                <h3 className="qa-quotations-cost-title">Cost breakdown</h3>
                {(parsedBreakdown ? [
                  { label: 'Base inspection fee', value: baseFee },
                  { label: 'Travel & logistics', value: travelFee },
                  { label: 'Regional taxes', value: taxesFee },
                  { label: 'Platform escrow fee', value: platformFee, highlight: true },
                ] : []).map((row) => (
                  <div
                    key={row.label}
                    className={`qa-quotations-cost-row ${row.highlight ? 'qa-quotations-cost-row--highlight' : ''}`}
                  >
                    <span className="qa-quotations-cost-label">{row.label}</span>
                    <span className="qa-quotations-cost-value">
                      {symbol}{Number(row.value).toFixed(2)}
                    </span>
                  </div>
                ))}
                {!parsedBreakdown && selectedQuotation && (
                  <div className="qa-quotations-cost-row">
                    <span className="qa-quotations-cost-label">Quote amount</span>
                    <span className="qa-quotations-cost-value">
                      {symbol}{Number(selectedQuotation.quote_amount).toFixed(2)}
                    </span>
                  </div>
                )}
              </div>

              <div className="qa-quotations-final-card">
                <span className="qa-quotations-final-label">Final payable amount</span>
                <span className="qa-quotations-final-amount">
                  {symbol}{Number(finalAmount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
                <span className="qa-quotations-final-icon" aria-hidden>✓</span>
              </div>

              {(() => {
                const status = (selectedQuotation?.status || '').toLowerCase();
                const isSelected = status === 'selected';
                const isRejected = status === 'rejected';
                const canAct = !isSelected && !isRejected;

                if (!canAct) {
                  return (
                    <div className="qa-quotations-status-message">
                      {isSelected && (
                        <>
                          {isSelectedPaid && !inspectionCompleted ? (
                            <p className="qa-quotations-status-text qa-quotations-status-text--approved">
                              Payment completed for this inspection.
                            </p>
                          ) : selectedP2pPayment?.waiting_for_inspector_accept ? (
                            <p className="qa-quotations-status-text qa-quotations-status-text--pending">
                              Quotation approved. Waiting for the inspector to accept the assignment
                              before payment is available.
                            </p>
                          ) : selectedP2pPayment?.payment_approval_pending ? (
                            <p className="qa-quotations-status-text qa-quotations-status-text--pending">
                              Quotation approved. Internal payment approval is in progress. Payment
                              Now will be enabled once all approval levels are complete and the
                              inspector has accepted the assignment.
                            </p>
                          ) : inspectionCompleted ? (
                            <p className="qa-quotations-status-text qa-quotations-status-text--approved">
                              The inspector has submitted the final report.
                              {buyerDecision
                                ? ` You ${buyerDecision.decision === 'accept' ? 'accepted' : 'rejected'} this report.`
                                : ' Please review the report and accept or reject it.'}
                            </p>
                          ) : (
                            <p className="qa-quotations-status-text qa-quotations-status-text--approved">
                              This quotation has been approved.
                              {canShowPaymentNow
                                ? ' You can complete payment below or view the invoice.'
                                : ' You can view the invoice from the button below.'}
                            </p>
                          )}
                          <div className="qa-quotations-post-approve-actions">
                            {showReviewReportAction ? (
                              <button
                                type="button"
                                className="qa-quotations-btn qa-quotations-btn-approve"
                                onClick={handleReviewReport}
                              >
                                Review inspection report
                              </button>
                            ) : null}
                            {showViewDecisionAction ? (
                              <button
                                type="button"
                                className="qa-quotations-btn qa-quotations-btn-invoice"
                                onClick={handleReviewReport}
                              >
                                View report & decision
                              </button>
                            ) : null}
                            {selectedQuotation?.inspection_id ? (
                              <button
                                type="button"
                                className="qa-quotations-btn qa-quotations-btn-invoice"
                                onClick={handleViewInvoice}
                              >
                                View invoice
                              </button>
                            ) : null}
                            {canShowPaymentNow ? (
                              <button
                                type="button"
                                className="qa-quotations-btn qa-quotations-btn-approve"
                                onClick={handlePaymentNow}
                              >
                                {selectedP2pPayment?.is_p2p_linked ? 'Payment now' : 'Proceed to payment'}
                              </button>
                            ) : null}
                          </div>
                        </>
                      )}
                      {isRejected && (
                        <p className="qa-quotations-status-text qa-quotations-status-text--rejected">
                          This quotation was rejected.
                        </p>
                      )}
                    </div>
                  );
                }

                return (
                  <div className="qa-quotations-actions">
                    <button
                      type="button"
                      className="qa-quotations-btn qa-quotations-btn-reject"
                      onClick={handleRejectQuotation}
                      disabled={rejectSubmitting}
                    >
                      {rejectSubmitting ? 'Rejecting…' : 'Reject quotation'}
                    </button>
                    <button
                      type="button"
                      className="qa-quotations-btn qa-quotations-btn-approve"
                      onClick={handleApproveAndPay}
                      disabled={approveSubmitting}
                    >
                      {approveSubmitting ? 'Approving…' : 'Approve & pay'}
                    </button>
                  </div>
                );
              })()}

              <button type="button" className="qa-quotations-clarification">
                <span className="qa-quotations-clarification-icon" aria-hidden>💬</span>
                <span className="qa-quotations-clarification-text">Ask for clarification</span>
              </button>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}
